import React, { useState, useRef, useEffect } from "react";
import styles from "./ToolSelector.module.css";

export type ToolInfo = {
  id: string;
  name: string;
  description: string;
  icon: string;
  supported_models: string[];
};

interface ToolSelectorProps {
  availableTools: ToolInfo[];
  selectedTools: string[];
  onToolsChange: (tools: string[]) => void;
  currentModel: string;
  disabled?: boolean;
}

const ICON_MAP: Record<string, React.ReactNode> = {
  chart: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M7 16V8" />
      <path d="M12 16v-5" />
      <path d="M17 16v-8" />
    </svg>
  ),
  search: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  ),
  document: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  ),
  image: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="9" cy="9" r="2" />
      <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
    </svg>
  ),
};

/* Gemini-style sliders icon */
const SlidersIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="4" y1="8" x2="20" y2="8" />
    <line x1="4" y1="16" x2="20" y2="16" />
    <circle cx="8" cy="8" r="2" fill="currentColor" stroke="none" />
    <circle cx="16" cy="16" r="2" fill="currentColor" stroke="none" />
  </svg>
);

const ToolSelector: React.FC<ToolSelectorProps> = ({
  availableTools,
  selectedTools,
  onToolsChange,
  currentModel,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const modelTools = availableTools.filter((t) =>
    t.supported_models.includes(currentModel),
  );

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen]);

  useEffect(() => {
    const supported = new Set(modelTools.map((t) => t.id));
    const filtered = selectedTools.filter((id) => supported.has(id));
    if (filtered.length !== selectedTools.length) {
      onToolsChange(filtered);
    }
  }, [currentModel]);

  // When disabled, clear any selected tools and don't render
    useEffect(() => {
        if (disabled && selectedTools.length > 0) {
            onToolsChange([]);
        }
    }, [disabled]);

  const toggleTool = (toolId: string) => {
    if (selectedTools.includes(toolId)) {
      onToolsChange(selectedTools.filter((id) => id !== toolId));
    } else {
      onToolsChange([...selectedTools, toolId]);
    }
  };

  const removeTool = (toolId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onToolsChange(selectedTools.filter((id) => id !== toolId));
  };

  if (modelTools.length === 0) return null;

  if (disabled) return null;

  const activeTools = modelTools.filter((t) => selectedTools.includes(t.id));
  const hasActive = activeTools.length > 0;

  return (
    <div ref={containerRef}>
      {/* Gemini-style tools button */}
      <button
        className={`${styles.toolsButton} ${hasActive ? styles.toolsButtonActive : ""}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Tools"
        disabled={disabled}
      >
        <SlidersIcon />
        <span className={styles.toolsButtonLabel}>Tools</span>
      </button>

      {/* Active tool chips */}
      {activeTools.map((tool) => (
        <span key={tool.id} className={styles.activeChip}>
          <span className={styles.activeChipIcon}>{ICON_MAP[tool.icon]}</span>
          <span className={styles.activeChipLabel}>{tool.name}</span>
          <button
            className={styles.activeChipRemove}
            onClick={(e) => removeTool(tool.id, e)}
            aria-label={`Remove ${tool.name}`}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </span>
      ))}

      {/* Dropdown panel */}
      {isOpen && (
        <div className={styles.dropdown}>
          <div className={styles.dropdownTitle}>Tools</div>
          <div className={styles.dropdownList}>
            {modelTools.map((tool) => {
              const isActive = selectedTools.includes(tool.id);
              return (
                <button
                  key={tool.id}
                  className={`${styles.dropdownRow} ${isActive ? styles.dropdownRowActive : ""}`}
                  onClick={() => toggleTool(tool.id)}
                >
                  <span
                    className={`${styles.dropdownRowIcon} ${isActive ? styles.dropdownRowIconActive : ""}`}
                  >
                    {ICON_MAP[tool.icon]}
                  </span>
                  <span className={styles.dropdownRowBody}>
                    <span className={styles.dropdownRowName}>{tool.name}</span>
                    <span className={styles.dropdownRowDesc}>
                      {tool.description}
                    </span>
                  </span>
                  <span
                    className={`${styles.dropdownRowToggle} ${isActive ? styles.dropdownRowToggleOn : ""}`}
                  >
                    <span className={styles.dropdownRowToggleThumb} />
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ToolSelector;
